<template xmlns:v-slot='http://www.w3.org/1999/XSL/Transform'>
<div class='h-width-100p'>
    <FwSpinLoader
        v-if='placeLoading'
        :colors='["#27dbbd", "#27DBBD", "#27DBBD"]'
        class='h-flex-center loader'
        :isActive='loading'
        className='h-p-20'>
    </FwSpinLoader>
    <FwSelect
        ref='autocomplete'
        v-model='value'
        v-focus='autofocus'
        class='fw-select-address__absolute b-select-address'
        :class='{
            // "b-select-address--with-start-address": startAddress && !value,
            "b-select-address--with-value": value,
            "b-select-address--loading": loading,
        }'
        :multiple='false'
        label='text'
        :clear-on-select='false'
        :propsPlaceholder='text'
        :searchable='true'
        :allowEmpty='true'
        :searchIcon='true'
        :closeOnSelect='true'
        noElementsFoundText=''
        :disabled='isDisabled'
        :internalSearch='false'
        :existedValue='!!startAddress'
        openDirection='bottom'
        :options='options'
        @search-change='searchChange'
        @open='hideMap = true'
        @select='getPlace'
        @removeValue='removeValue'>
        <template v-slot:option='{ props }'>
            <FwSpinLoader
                v-if='loading'
                :colors='["#27dbbd", "#27DBBD", "#27DBBD"]'
                class='h-flex-center loader'
                :isActive='loading'
                className='h-p-20'>
            </FwSpinLoader>
            <slot
                :props='props'
                name='option'>
                <div :style='{ opacity: loading ? 0 : 1 }'
                     class='multiselect__content-item'>
                    <div class='multiselect__item-header h-flex h-flex-dir-column h-max-width-100p'>
                        <span>
                            {{ props.option.city }} {{ props.option.country }}
                        </span>
                    </div>
                </div>
            </slot>
        </template>
        <template #noResult>
            <span></span>
        </template>
        <template #noOptions>
            <span></span>
        </template>
    </FwSelect>

    <div v-if='!isSearchQueryShort && !options.length && !loading'>
        <div class='b-select-address--no-result'>
            {{ $t('SELECTOR.NO.ELEMENT.FOUND') }}
        </div>
    </div>
    <div v-else-if='options.length'>
        <p class='h-mt-20 h-fw-500 h-font-14 h-opacity-0_5'>
            {{ $t('EVENT.NAME.LOCATION.RESULT') }}:
        </p>
    </div>

    <div v-if='postCodeCannotDetect'
         class='h-flex h-flex-space-between h-mb-20 h-mt-20'>
        <FwFormInput
            class='h-flex-1-0'
            labelType='label-medium'
            :title='`${$t(`LABEL.ZIP.CODE`)}*`'>
            <FwInput
                :value='post_code'
                type='number'
                name='text'
                :placeholder='$t(`LABEL.ZIP.CODE`)'
                inputStyle='white-bigger'
                @input='value => changePostCodeValue(value)'>
            </FwInput>
        </FwFormInput>
    </div>
    <div v-if='!hideMap'>
        <div v-if='mapsApi_isGoogleMap'
             ref='map'
             style='opacity: 0; position: fixed; left: -100%'
             class='b-branch-office-map-size'>
        </div>
        <GlobalMap
            v-if='coords'
            :latitude='coords.latitude'
            :longitude='coords.longitude'>
        </GlobalMap>
    </div>
</div>
</template>

<script lang='ts'>
import { Component, Emit, mixins, Prop, Vue } from 'vue-facing-decorator';
import { AutoCompleteItemType } from '@/types/Address';
import { SelectAddressOptionType } from '@/types/Appointment';
import { GlobalMap } from '@/components/common/GlobalMap';
import GoogleApi from '@/mixins/google-api';
import MapsApi from '@/mixins/maps-api';
import { GoogleMapPlaceType, ParsedAddressData } from '@/types/GoogleMap';
import { parseGooglePlaceAsAddress } from '@/helpers/google';
import { Debounce } from '@/helpers/decorators/debounce';
import { parseAutocompleteWithApiAsAddress } from '@/helpers/autocomplete';
import { AutocompleteWebApi } from '@/api/autocomplete/AutocompleteApi';

const MIN_GOOGLE_AUTO_COMPLETE_REQUEST_LETTERS_LENGTH = 7;

type GoogleMapItemType = {
    description: string
    matched_substrings: Array<{ length: number, offset: number }>,
    place_id: string
    reference: string
    structured_formatting: {
        main_text: string
        main_text_matched_substrings: Array<{ length: number, offset: number }>
        secondary_text: string
    }
    terms: Array<{ value: string, offset: number }>
    types: Array<string>
}

@Component({
    components: {
        GlobalMap,
    },
    refs: [`autocomplete`, `map`],
})
export default class SelectAddress extends mixins(MapsApi, GoogleApi) {
    @Prop({ type: String, default: null }) readonly startAddress!: string | null;
    @Prop({ type: String, default: 'SELECT.ADDRESS.APPOINTMENT.PLACEHOLDER' }) readonly placeholderText!: string | null;
    @Prop({ type: Boolean, default: false }) readonly isDisabled!: boolean;
    @Prop({ type: Boolean, default: false }) readonly autofocus!: boolean;
    @Prop({ type: Boolean, default: false }) readonly postCodeNotNeed!: boolean;

    options: Array<SelectAddressOptionType> = [];
    value: AutoCompleteItemType | null = null;
    currentPlace: ParsedAddressData | null = null;
    search: string = '';
    loading: boolean = false;
    placeLoading: boolean = false;
    hideMap: boolean = false;
    postCodeCannotDetect: boolean = false;
    autocompleteService: any = null;
    post_code: string = '';
    placeGetParam: string = `language=en&libraries=places&callback=logPlaceDetails`;
    scriptLogPlaceDetails: HTMLScriptElement | null = null;
    mapGetParam: string = `sensor=false&language=en&libraries=places&types=postal_code`;

    get refs() {
        return this.$refs as {
            autocomplete: Vue
            map: HTMLElement
        };
    }

    get coords(): null | { latitude: number, longitude: number } {
        if (this.mapsApi_isGoogleMap && this.currentPlace && this.currentPlace.latitude && this.currentPlace.longitude) {
            return {
                latitude: this.currentPlace.latitude,
                longitude: this.currentPlace.longitude,
            };
        } else if (this.mapsApi_isLeafletMap && this.value) {
            return {
                latitude: this.value.latitude,
                longitude: this.value.longitude,
            };
        }
        return null;
    }

    get text() {
        if (this.startAddress) {
            return this.startAddress;
        }
        if (this.value && typeof this.value === `string`) {
            return this.value;
        }
        // @ts-ignore-next-line
        return this.value ? this.value.text : this.$t(this.placeholderText);
    }

    get isSearchQueryShort(): boolean {
        return !this.search || this.search.length < MIN_GOOGLE_AUTO_COMPLETE_REQUEST_LETTERS_LENGTH;
    }

    searchChange(search: string) {
        this.search = search;
    }

    displayGoogleSuggestions(predictions: Array<GoogleMapItemType>) {
        if (!predictions || !predictions.length) {
            this.loading = false;
            return;
        }
        this.options = predictions.map((item: GoogleMapItemType) => ({
            place_id: item.place_id,
            city: item.structured_formatting.main_text,
            country: item.structured_formatting.secondary_text,
            text: `${item.structured_formatting.main_text}, ${item.structured_formatting.secondary_text}`,
        }));
        this.loading = false;
    }

    getPlaceGoogle(value: SelectAddressOptionType): void {
        this.placeLoading = true;
        // @ts-ignore-next-line
        window.logPlaceDetails = () => {
            const service = new window.google.maps.places.PlacesService(this.$refs.map as any);
            service.getDetails({
                placeId: value.place_id,
                fields: ['address_components', 'geometry', 'formatted_address'],
            }, (place: any) => {
                this.sendPlaceHandler(place);
                const parsedAddressData: ParsedAddressData = parseGooglePlaceAsAddress(place);
                this.currentPlace = parsedAddressData;
                if (!parsedAddressData.post_code && !this.postCodeNotNeed) {
                    this.postCodeCannotDetect = true;
                    this.sentNotif(`LABEL.ADDRESS.POST.CODE`);
                } else {
                    this.postCodeCannotDetect = false;
                }
                this.inputHandler(parsedAddressData);
                this.placeLoading = false;
            });
        };
        this.removeGoogleMapScript();
        this.scriptLogPlaceDetails = document.createElement(`script`);
        this.scriptLogPlaceDetails.type = `text/javascript`;
        this.scriptLogPlaceDetails.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleApiKey}&${this.placeGetParam}`;
        this.scriptLogPlaceDetails.async = true;
        document.head.appendChild(this.scriptLogPlaceDetails);
    }

    async getPlaceByCountry(value: AutoCompleteItemType) {
        let place = value;
        if (place) {
            if (!(place.latitude && place.longitude) && place.id) {
                place = await AutocompleteWebApi.fetchPlaceById({
                    id: place.id,
                });
            }
            if (!place.post_code) {
                this.postCodeCannotDetect = true;
                this.sentNotif(`LABEL.ADDRESS.POST.CODE`);
            } else {
                this.postCodeCannotDetect = false;
            }
            const parsedAddressData = parseAutocompleteWithApiAsAddress({ ...place, country: 'France' });
            this.inputHandler(parsedAddressData);
        }
    }

    getPlace(value: SelectAddressOptionType | AutoCompleteItemType) {
        if (this.mapsApi_isGoogleMap) {
            this.getPlaceGoogle((value as SelectAddressOptionType));
        } else if (this.mapsApi_isLeafletMap) {
            this.getPlaceByCountry((value as AutoCompleteItemType));
        }
        this.hideMap = false;
    }

    mountGoogleMaps(): void {
        const multiselect: HTMLInputElement = (this.$refs?.autocomplete?.$refs?.multiselect?.$refs?.search as HTMLInputElement);
        // @ts-ignore
        if (multiselect) {
            // @ts-ignore
            if (this.mapsApi_isGoogleMap) {
                // @ts-ignore-next-line
                this.autocompleteService = new window.google.maps.places.AutocompleteService((multiselect),);
            }
            const inputHandler = async() => {
                await this.$nextTick();
                if (!this.isSearchQueryShort) {
                    this.doSearchDebounceImmediate(multiselect);
                } else {
                    this.options = [];
                }
            };

            multiselect.addEventListener('input', inputHandler);
            multiselect.addEventListener('paste', inputHandler);
        }
    }

    removeValue() {
        this.inputHandler(null);
    }

    displaySuggestions(predictions: Array<AutoCompleteItemType>) {
        if (!predictions || !predictions.length) {
            this.loading = false;
            return;
        }
        // @ts-ignore-next-line
        this.options = predictions.map((item: AutoCompleteItemType) => (item.label ? {
            country: item.label,
            text: item.label,
            ...item.id ? { id: item.id } : {},
            ...item,
        } : {}));
        this.loading = false;
    }

    @Debounce(600)
    async doSearchDebounceImmediate(multiselect: HTMLInputElement) {
        if (this.search) {
            this.loading = true;
            if (this.mapsApi_isGoogleMap) {
                await this.autocompleteService.getPlacePredictions({
                    types: ['address'],
                    input: this.search,
                    componentRestrictions: { country: `FR` },
                }, this.displayGoogleSuggestions);
            } else if (this.mapsApi_isLeafletMap) {
                // @ts-ignore-next-line
                const result = await AutocompleteWebApi.getFrAddresses({ q: this.search });
                this.displaySuggestions(result);
            }
            multiselect.focus();
        }
    }

    changePostCodeValue(value: string) {
        this.post_code = value;
        this.changePostCodeHandler(value);
    }

    @Emit('changePostCode')
    changePostCodeHandler(value: string): string {
        return value;
    }

    @Emit('input')
    inputHandler(parsedAddressData: ParsedAddressData | null): ParsedAddressData | null {
        return parsedAddressData;
    }

    @Emit('sendPlace')
    sendPlaceHandler(place: GoogleMapPlaceType): GoogleMapPlaceType {
        return place;
    }
}
</script>

<style lang='sass'>
.pac-container
    display: none !important

.b-select-address

    .multiselect__content
        overflow-y: auto
        max-height: 450px

    .multiselect__tags
        border-radius: 10px !important

        .multiselect__input::placeholder
            text-overflow: ellipsis
            width: 80%
            overflow: hidden
            white-space: nowrap

    &.b-select-address--empty

        .multiselect__content-wrapper
            display: none !important

    .multiselect__content-wrapper
        display: flex
        width: 100% !important

        .multiselect__content-item
            display: flex

            .multiselect__item-header
                width: 100%
                white-space: nowrap
                overflow-x: hidden
                text-overflow: ellipsis
                max-width: 315px

    &--with-start-address

        .multiselect__placeholder
            color: #203f6a !important
            text-overflow: ellipsis
            width: 80%
            overflow: hidden
            white-space: nowrap

        .multiselect__option
            width: 100%
            display: none !important

    &--search-query-short

        .multiselect__option
            display: none !important

    &--loading

        .multiselect__option
            display: none !important

    &--no-result
        background-color: #fff
        padding: 10px
        border-radius: 4px
        box-shadow: 0 12px 24px 0 rgba(0, 0, 0, 0.05)
</style>
